/**
 * Inventory API
 * Fetches sites and tools for the Inventory screens.
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * Fetch all sites from the Sites container.
 * Returns raw Cosmos documents with: site_id, site_name, site_city,
 * site_state_province, oem_primary, supplier_site_code, active.
 */
export async function getSites(callerEmail = null, module = null) {
  const params = new URLSearchParams();
  if (callerEmail) params.append('caller_email', callerEmail);
  if (module) params.append('module', module);

  const url = `${API_BASE_URL}/get_sites${params.toString() ? '?' + params.toString() : ''}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  return response.json();
}

/**
 * Fetch tools for a specific site.
 * Uses the get_tools endpoint with site_id filter.
 * Returns raw Cosmos tool documents.
 */
export async function getToolsBySite(siteId, callerEmail = null) {
  const params = new URLSearchParams({ site_id: siteId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const url = `${API_BASE_URL}/get_tools?${params.toString()}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  return response.json();
}

/**
 * Transform a raw Cosmos tool document into the shape
 * expected by the ToolListScreen and ToolDetailScreen.
 */
export function transformToolForInventory(tool) {
  // Derive inventory status from flags
  let status = 'NOT YET';
  if (tool.found) status = 'FOUND';
  else if (tool.added_by_tmg) status = 'ADDED';

  return {
    id: tool.id || tool.tool_id,
    toolId: tool.supplier_tool_id || tool.tool_id,
    altId: tool.alt_tool_id || null,
    toolType: tool.tool_type || '',
    opNumber: tool.die_op || null,
    status,
    photoCount: 0, // photos loaded separately
    partNumbers: [],
    assetTags: [],
    siteLocation: tool.site_location || '',
    detailLocation: tool.detail_location || '',
    rowNbr: tool.row_nbr ?? null,
    stackNbr: tool.stack_nbr ?? null,
    rackNbr: tool.rack_nbr ?? null,
    shelfNbr: tool.shelf_nbr ?? null,
    skidNbr: tool.skid_nbr ?? null,
    workflowStatus: tool.workflow_status || '',
    // Extended fields for pinned-column display
    quantity: tool.quantity ?? '',
    cavities: tool.cavities ?? '',
    toolCode: tool.tool_code || '',
    length: tool.length ?? '',
    width: tool.width ?? '',
    height: tool.height ?? '',
    estimatedWeight: tool.estimated_weight ?? '',
    weightUnit: tool.weight_unit || '',
  };
}

/**
 * Fetch a single tool by its internal Cosmos tool_id.
 * Returns the raw Cosmos document.
 */
export async function getToolDetail(toolId, callerEmail = null) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const url = `${API_BASE_URL}/get_tools?${params.toString()}`;
  const response = await apiFetch(url);
  if (!response.ok) throw new Error(`API error: ${response.status}`);

  const tools = await response.json();
  if (!tools.length) throw new Error('Tool not found');
  return tools[0];
}

/**
 * PATCH an existing tool via update_tool.
 * Sends snake_case fields directly (inventory form state matches Cosmos schema).
 * Returns the updated Cosmos document.
 */
export async function updateInventoryTool(toolId, fields, callerEmail = null) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);

  const response = await apiFetch(`${API_BASE_URL}/update_tool?${params}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Save failed (${response.status})`);
  }

  return response.json();
}

/**
 * POST a new tool document to Cosmos.
 * Returns the created document (includes id, tool_id).
 */
export async function createInventoryTool(siteId, supplierToolId, oem, callerEmail) {
  const response = await apiFetch(`${API_BASE_URL}/update_tool`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      site_id: siteId,
      supplier_tool_id: supplierToolId || null,
      oem: oem || null,
      created_by: callerEmail,
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || err.error || `Create failed (${response.status})`);
  }

  return response.json();
}

/**
 * Multi-identifier search across Tools, AssetTags, PartNumbers.
 * Returns matching tool documents for a site.
 */
export async function searchInventoryTools(siteId, query, callerEmail) {
  const params = new URLSearchParams({
    site_id: siteId, q: query, caller_email: callerEmail,
  });
  const res = await apiFetch(`${API_BASE_URL}/search_inventory?${params}`);
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

/* ── Media / Photos ────────────────────────────────────────────────────── */
// Canonical implementations live in src/shared/services/mediaApi.js so the
// shared PhotoGalleryPanel can import them without crossing the shared/→
// features/ boundary. Re-exported here for backward compat with existing
// feature-side callers (e.g. ToolDetailScreen).

export { getMedia, deleteMedia } from '../../../shared/services/mediaApi';

/* ── Part Numbers ──────────────────────────────────────────────────────── */

export async function getPartNumbers(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?${params}`);
  if (!res.ok) throw new Error('Failed to fetch part numbers');
  return res.json();
}

export async function addPartNumber({ toolId, siteId, partNumber, partNumberType, partDescription, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tool_id: toolId, site_id: siteId, part_number: partNumber,
      part_number_type: partNumberType || '', part_description: partDescription || '',
      caller_email: callerEmail,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to add part number');
  }
  return res.json();
}

export async function patchPartNumber({ partNumberId, partNumber, partNumberType, partDescription, callerEmail }) {
  const body = { part_number_id: partNumberId, caller_email: callerEmail };
  if (partNumber !== undefined) body.part_number = partNumber;
  if (partNumberType !== undefined) body.part_number_type = partNumberType;
  if (partDescription !== undefined) body.part_description = partDescription;
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update part number');
  }
  return res.json();
}

export async function deletePartNumber({ partNumberId, toolId, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_part_number?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ part_number_id: partNumberId, tool_id: toolId, caller_email: callerEmail }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete part number');
  }
  return res.json();
}

/* ── Asset Tags ────────────────────────────────────────────────────────── */

export async function getAssetTags(toolId, callerEmail) {
  const params = new URLSearchParams({ tool_id: toolId });
  if (callerEmail) params.append('caller_email', callerEmail);
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?${params}`);
  if (!res.ok) throw new Error('Failed to fetch asset tags');
  return res.json();
}

export async function addAssetTag({ toolId, siteId, tagValue, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tool_id: toolId, site_id: siteId, tag_value: tagValue, caller_email: callerEmail }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to add asset tag');
  }
  return res.json();
}

export async function patchAssetTag({ tagId, tagValue, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag_id: tagId, tag_value: tagValue, caller_email: callerEmail }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update asset tag');
  }
  return res.json();
}

export async function deleteAssetTag({ tagId, toolId, callerEmail }) {
  const res = await apiFetch(`${API_BASE_URL}/manage_asset_tag?caller_email=${encodeURIComponent(callerEmail)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tag_id: tagId, tool_id: toolId, caller_email: callerEmail }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete asset tag');
  }
  return res.json();
}
